
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Grid, TextField, Checkbox, FormControlLabel,
    Button, CircularProgress, Divider
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import apiUrl from '../../Config';
import { toast } from 'react-toastify';

const AddEditAddOnService = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { isEditMode, service } = location.state || {};


    const [formData, setFormData] = useState({
        id: '',
        name: '',
        description: '',
        price: '',
        durationMinutes: '',
        serviceImg: null,
        isActive: false,
        isDeleted: false,
        sequenceOrder: '',
    });

    const [imagePreview, setImagePreview] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [validateErrors, setValidateErrors] = useState({})

    // Populate form on edit
    useEffect(() => {
        if (service) {
            setFormData({
                id: service.id || '',
                name: service.name || '',
                description: service.description || '',
                price: service.price || '',
                durationMinutes: service.durationMinutes || '',
                serviceImg: null,
                isActive: service.isActive || false,
                isDeleted: service.isDeleted || false,
                sequenceOrder: service.sequenceOrder || '',
            });

            // If image URL is present (string), set as preview
            if (service.serviceImg && typeof service.serviceImg === 'string') {
                setImagePreview(service.serviceImg);
            } else if (service.imageUrl) {
                // Or if imageUrl prop exists in your data
                setImagePreview(service.imageUrl);
            }
        }
    }, [service]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (['price', 'durationMinutes', 'sequenceOrder'].includes(name)) {
            // Allow only numbers and empty string
            if (value === '' || /^[0-9\b]+$/.test(value)) {
                setFormData(prev => ({ ...prev, [name]: value }));
                setValidateErrors(prev => ({ ...prev, [name]: '' }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
            setValidateErrors(prev => ({ ...prev, [name]: '' }))
        }
        setError('');
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const handleImageFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData((prev) => ({ ...prev, serviceImg: file }));
            setImagePreview(URL.createObjectURL(file));
            setValidateErrors(prev => ({ ...prev, serviceImg: '' }));
            setError('');
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.name?.trim()) errors.name = "Name is required";
        if (!formData.description?.trim()) errors.description = "Description is required";
        if (!formData.price || isNaN(formData.price)) errors.price = "Valid price is required";
        if (!formData.durationMinutes || isNaN(formData.durationMinutes)) errors.durationMinutes = "Valid duration is required";
        if (!formData.sequenceOrder || isNaN(formData.sequenceOrder)) errors.sequenceOrder = "Valid sequence order is required";

        if (!formData.serviceImg && !imagePreview) errors.serviceImg = "Please upload a valid image file";

        return errors;
    };



    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setValidateErrors(errors);
            return;
        }

        setLoading(true);
        try {
            const payload = new FormData();
            payload.append('id', formData.id);
            payload.append('name', formData.name);
            payload.append('description', formData.description);
            payload.append('price', formData.price)

            payload.append('durationMinutes', formData.durationMinutes)


            if (formData.serviceImg instanceof File) {
                payload.append('addOnServiceImage', formData.serviceImg);
            } else if (typeof imagePreview === 'string') {
                payload.append('addOnServiceImage', imagePreview);
            }
            payload.append('isActive', formData.isActive);
            payload.append('isDeleted', formData.isDeleted);
            payload.append('sequenceOrder', formData.sequenceOrder);

            const response = isEditMode
                ? await axios.put(`${apiUrl}/addonservices/updateaddonservice/${formData.id}`, payload)
                : await axios.post(`${apiUrl}/addonservices/createaddonservice`, payload);

            if (response.data.status === true) {
                navigate(-1);
                toast.success(response.data.message)
            } 
        } catch (err) {
            setError('Something went wrong. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 2 } }}>
            <Typography variant="h4" fontWeight={400} mb={2} >
                {isEditMode ? `Edit Addon Service - ${formData.name}` : 'Add New Addon Service'}
            </Typography>
            <Divider sx={{ mb: 1 }} />

            <form onSubmit={handleSubmit} >
                <Grid container spacing={2}>

                    {/* Image Upload */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body1" fontWeight={500} gutterBottom>
                            Service Image
                        </Typography>
                        <Box
                            sx={{
                                width: 120,
                                height: 120,
                                borderRadius: 2,
                                bgcolor: 'primary.background',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                 border: validateErrors.serviceImg ? '1px solid red' : '1px dashed gray',
                                mb: 1,
                            }}
                        >
                            {imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    style={{ width: '110px', height: '110px', borderRadius: 4 }}
                                />
                            ) : (
                                <Typography variant="caption">No Image</Typography>
                            )}
                        </Box>

                        <label htmlFor="upload-img">
                            <input
                                type="file"
                                id="upload-img"
                                hidden
                                accept="image/*"
                                onChange={handleImageFileChange}
                            />
                            <Button variant="outlined" component="span" type="button">
                                Upload Image
                            </Button>
                        </label>
                    </Grid> <br />

                    {/* Name */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Name"
                            name="name"

                            fullWidth
                            size="small"
                            value={formData.name}
                            onChange={handleInputChange}
                            error={!!validateErrors.name}
                            helperText={validateErrors.name}
                        />
                    </Grid>

                    {/* Description */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Description"
                            name="description"
                    
                            fullWidth
                            size="small"
                            value={formData.description}
                            onChange={handleInputChange}
                             error={!!validateErrors.description}
                            helperText={validateErrors.description}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Price"
                            name="price"
                            
                            fullWidth
                            size="small"
                            value={formData.price}
                            onChange={handleInputChange}
                             error={!!validateErrors.price}
                            helperText={validateErrors.price}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Duration Minutes"
                            name="durationMinutes"
                            
                            fullWidth
                            size="small"
                            value={formData.durationMinutes}
                            onChange={handleInputChange}
                             error={!!validateErrors.durationMinutes}
                            helperText={validateErrors.durationMinutes}
                        />
                    </Grid>

                    {/* Sequence Order */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Sequence Order"
                            name="sequenceOrder"
                            
                            fullWidth
                            size="small"
                            value={formData.sequenceOrder}
                            onChange={handleInputChange}
                             error={!!validateErrors.sequenceOrder}
                            helperText={validateErrors.sequenceOrder}
                        />
                    </Grid>

                    {/* Checkboxes */}
                    <Grid size={{ xs: 12, sm: 12 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="isActive"
                                    checked={formData.isActive}
                                    onChange={handleCheckboxChange}
                                    size="small"
                                />
                            }
                            label="Is Active"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="isDeleted"
                                    checked={formData.isDeleted}
                                    onChange={handleCheckboxChange}
                                    size="small"
                                />
                            }
                            label="Is Deleted"
                        />
                    </Grid>

                    {/* Error Message */}
                    {error && (

                        <Typography color="error" textAlign="left">
                            {error}
                        </Typography>

                    )}

                    {/* Submit Button */}
                    <Grid size={{ xs: 12 }}>
                        <Box display="flex" justifyContent="end" gap={2}>
                            <Button variant="outlined" color="primary" onClick={() => navigate(-1)}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" disabled={loading}>
                                {loading ? (
                                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                                ) : (
                                    isEditMode ? 'Update' : 'Create'
                                )}
                            </Button>
                        </Box>
                    </Grid>

                </Grid>
            </form>
        </Box>
    );
};

export default AddEditAddOnService;

